import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import {
  CarouselListOption,
  convertRecommendationsToCarouselListOptions,
  getCarouselItemSubtitle,
} from '@/contexts/item-recommendations-utils';
import { createStyles } from '@/contexts/theme-context';
import {
  FlatList,
  ListRenderItem,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { FocusSource, useComponentFocus } from '@/hooks/use-component-focus';
import { HorizontalList } from '@/components/horizontal-list';
import { ItemListOption } from '@/contexts/item-context-types';
import {
  LayoutSize,
  useLayoutContext,
  useLayoutSizeValue,
  useSmallLayoutSizeValue,
} from '@/contexts/layout-context';
import { NavigationArrows } from '@/components/navigation-arrows';
import { NavigationDots } from '@/components/navigation-dots';
import { TYPOGRAPHY } from '@/styles';
import { useCarouselItemShadowStyles } from '@/hooks/use-carousel-item-shadow-styles';
import { useItemDetailContext } from '@/contexts/item-detail-context';

interface Props {
  items: ItemListOption[];
  numRows?: number;
  onSeeAllPress: () => void;
}

export const ITEM_RECOMMENDATIONS_CAROUSEL_PREFIX =
  'item_recommendations_carousel';

const LIST_FOOTER_HEIGHT = 24;
export const RECOMMENDATIONS_LIMIT = 20;
const ITEM_IMAGE_SIZE = 48;
const ITEM_IMAGE_MARGIN_RIGHT = 10;
const CAROUSEL_ITEM_MARGIN = 4;

export const ItemRecommendationCarousel: React.FC<Props> = ({
  items,
  numRows = 2,
  onSeeAllPress,
}) => {
  const { appDimensions, isSmallLayout } = useLayoutContext();
  const [styles, theme] = useStyles();
  const [shadowStyles] = useCarouselItemShadowStyles();

  const listRef = useRef<FlatList>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [layoutWidth, setLayoutWidth] = useState(appDimensions.width);
  const [seenRecommendations, setSeenRecommendations] = useState<Set<string>>(
    new Set(),
  );

  const recommendations = items.slice(0, RECOMMENDATIONS_LIMIT);
  const numRecommendations = recommendations.length;

  const carouselContainerOuterPadding = isSmallLayout ? 16 : 32;

  const LAYOUT_SIZE_TO_COLUMNS_MAP = {
    [LayoutSize.SMALL]: 2,
    [LayoutSize.SMALL_TABLET]: 2,
    [LayoutSize.MEDIUM]: 3,
    [LayoutSize.LARGE]: 4,
  };

  const columnCountTotal = Math.ceil(numRecommendations / numRows);
  const columnCount = useLayoutSizeValue(LAYOUT_SIZE_TO_COLUMNS_MAP);

  const listItemWidth = useMemo(() => {
    const baseWidth =
      (layoutWidth -
        CAROUSEL_ITEM_MARGIN * 2 -
        2 * carouselContainerOuterPadding) /
      columnCount;
    // Account for additional width of footer on large layouts
    return isSmallLayout ? baseWidth : baseWidth - 4;
  }, [columnCount, carouselContainerOuterPadding, isSmallLayout, layoutWidth]);

  // Number of columns to move by when swiping or navigating within the carousel
  const carouselStrideLength = useSmallLayoutSizeValue(2, 1);

  const navigationCount = useMemo(() => {
    // For small screens, navigation within the carousel moves by two
    // For larger screens, navigation moves by one
    return isSmallLayout
      ? Math.ceil(columnCountTotal / carouselStrideLength)
      : columnCountTotal - (columnCount - 1);
  }, [columnCount, columnCountTotal, isSmallLayout, carouselStrideLength]);

  const handleSetActiveIndex = useCallback(
    (index: number, isSwipeEvent = false) => {
      setActiveIndex((prevValue) => {
        // Condition 1: Prevent extraneous scroll events sent by FlatList from being tracked
        // Condition 2: If final scroll position only includes one column, it doesn't register as a new index here, but should be tracked
        if (prevValue === index && index !== navigationCount) {
          return index;
        }
        const navDirection = index > prevValue ? 'forward' : 'backward';
        let analyticsEventName = ITEM_RECOMMENDATIONS_CAROUSEL_PREFIX;
        if (isSwipeEvent) {
          analyticsEventName = `${analyticsEventName}.swipe_${navDirection}`;
        } else {
          const navComponentName = isSmallLayout ? 'dot' : 'arrow';
          analyticsEventName = `${analyticsEventName}.navigation_${navComponentName}_${navDirection}.press`;
        }
        Analytics.track(analyticsEventName, {
          columnCountTotal,
          index,
        });
        return index;
      });
    },
    [setActiveIndex, isSmallLayout, navigationCount, columnCountTotal],
  );

  const scrollToIndex = useCallback(
    (index: number) => {
      listRef.current?.scrollToIndex({
        animated: true,
        index,
      });
      handleSetActiveIndex(index);
    },
    [handleSetActiveIndex],
  );

  const { selectItemListOption } = useItemDetailContext();
  const renderItem: ListRenderItem<CarouselListOption> = useCallback(
    (option) => {
      const itemsArr = option.item.metadata!.items;
      return (
        <View collapsable={false}>
          {itemsArr.map((item: ItemListOption, index: number) => {
            const uri = item.metadata?.photoURLs[0];
            return (
              <TouchableOpacity
                key={item.metadata?.variantPackUUID}
                onPress={() => {
                  selectItemListOption(item, {
                    itemRecommendationUUID:
                      item.metadata?.itemRecommendationUUID,
                    itemUUID: item.metadata?.itemUUID,
                    source: ITEM_RECOMMENDATIONS_CAROUSEL_PREFIX,
                  });
                }}
                style={[
                  styles.carouselItem,
                  shadowStyles.shadow,
                  { width: listItemWidth },
                ]}
              >
                <View
                  style={[
                    styles.imageContainer,
                    uri ? null : styles.imageContainerEmpty,
                  ]}
                >
                  {uri ? (
                    <CachedImage
                      alt={`item recommendation image ${index + 1} of ${itemsArr.length}`}
                      contentFit="contain"
                      source={{ uri }}
                      style={styles.image}
                    />
                  ) : null}
                </View>
                <View style={styles.carouselItemTextContainer}>
                  <Text numberOfLines={1} style={TYPOGRAPHY.pSemiBold}>
                    {item.title}
                  </Text>
                  <Text numberOfLines={1} style={TYPOGRAPHY.listSubtitle}>
                    {getCarouselItemSubtitle(item.metadata?.skus!)}
                  </Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      );
    },
    [listItemWidth, selectItemListOption, styles, shadowStyles],
  );

  useComponentFocus({
    onFocus: useCallback(
      (focusSource: FocusSource) => {
        if (focusSource === FocusSource.Navigation) {
          // Scroll position is lost after navigation, call it explicitly again to reset
          scrollToIndex(activeIndex);
          setSeenRecommendations(new Set());
        }
      },
      [scrollToIndex, activeIndex],
    ),
  });

  const carouselListOptions: CarouselListOption[] = useMemo(() => {
    return convertRecommendationsToCarouselListOptions(
      recommendations,
      numRows,
    );
  }, [numRows, recommendations]);

  useEffect(() => {
    const startIndex = activeIndex * 2;
    const endIndex = columnCount * 2 + startIndex;
    const visibleRecommendations = recommendations.slice(startIndex, endIndex);
    for (const { metadata } of visibleRecommendations) {
      const itemRecommendationUUID = metadata!.itemRecommendationUUID!;
      if (!seenRecommendations.has(itemRecommendationUUID)) {
        seenRecommendations.add(itemRecommendationUUID);
        Analytics.track(
          `${ITEM_RECOMMENDATIONS_CAROUSEL_PREFIX}.recommendation_card.view`,
          {
            itemRecommendationUUID,
            itemUUID: metadata!.itemUUID,
            variantPackUUID: metadata!.variantPackUUID,
          },
        );
      }
    }
  }, [recommendations, columnCount, activeIndex, seenRecommendations]);

  return (
    <View
      collapsable={false}
      onLayout={(e) => setLayoutWidth(e.nativeEvent.layout.width)}
    >
      <HorizontalList<CarouselListOption>
        data={carouselListOptions}
        getItemLayout={(_, index) => ({
          index,
          length: listItemWidth,
          offset: listItemWidth * index * carouselStrideLength,
        })}
        itemCount={numRecommendations}
        keyExtractor={({ value }) => value.toString()}
        limit={RECOMMENDATIONS_LIMIT}
        listRef={listRef}
        onMomentumScrollEnd={(e: NativeSyntheticEvent<NativeScrollEvent>) => {
          const newIndex = Math.round(
            e.nativeEvent.contentOffset.x /
              listItemWidth /
              carouselStrideLength,
          );
          handleSetActiveIndex(newIndex, true);
        }}
        onSeeAllPress={onSeeAllPress}
        renderItem={renderItem}
        scrollEnabled={Platform.OS !== 'web'}
        // onMomentumScrollEnd doesn't work on web
        // https://github.com/necolas/react-native-web/issues/1021
        scrollEventThrottle={100}
        style={[
          Platform.OS !== 'android' ? styles.list : null,
          {
            marginHorizontal:
              // Subtract node outer padding to account for additional space needed for dropshadow to appear
              carouselContainerOuterPadding - CAROUSEL_ITEM_MARGIN,
          },
        ]}
        title={i18n.t('based_on_history')}
      />
      {/* If some columns are not visible */}
      {columnCountTotal > columnCount ? (
        isSmallLayout ? (
          <View style={styles.footer}>
            <NavigationDots
              activeIndex={activeIndex}
              count={navigationCount}
              onChange={scrollToIndex}
            />
          </View>
        ) : (
          <NavigationArrows
            activeIndex={activeIndex}
            backgroundColor={theme.colors.white}
            buttonContainerStyle={styles.navigationButton}
            count={navigationCount}
            onChange={scrollToIndex}
          />
        )
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  carouselItem: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: 4,
    flexDirection: 'row',
    margin: 4,
    padding: 8,
  },
  carouselItemTextContainer: {
    flex: 1,
  },
  footer: {
    height: LIST_FOOTER_HEIGHT,
    justifyContent: 'center',
    marginTop: -6,
  },
  image: {
    height: ITEM_IMAGE_SIZE,
    width: ITEM_IMAGE_SIZE,
  },
  imageContainer: {
    marginRight: ITEM_IMAGE_MARGIN_RIGHT,
  },
  imageContainerEmpty: {
    height: ITEM_IMAGE_SIZE,
  },
  list: {
    overflow: 'visible',
  },
  navigationButton: {
    height: 250,
  },
}));
